// Prepares a picked video for upload (spec 2026-10-23 "Compression", web): probe it, take the poster
// from the source at once, decide (videoDecision), then compress with mediabunny on WebCodecs, remux,
// or send it as it is. mediabunny sits behind `MediaEngine` (mediabunnyEngine.ts, loaded lazily) so the
// rule and its fallbacks are unit-tested without WebCodecs (not in jsdom).
import {
  DEFAULT_SPEED_WEB_HW,
  MIME_AAC,
  VideoRule,
  afterEncode,
  afterFailure,
  decide,
  expectedBytes,
  keepCompressing,
  type VideoPlan,
  type VideoSource,
} from "./videoDecision";

export interface PosterFrame {
  blob: Blob;
  width: number;
  height: number;
}

export interface VideoProbe {
  source: VideoSource;
  /** Rotation-aware size (what the viewer sees). */
  displayWidth: number;
  displayHeight: number;
  /** Average frame rate, null = unknown. */
  fps: number | null;
}

export interface EncoderSupport {
  /** H.264 can be encoded (WebCodecs). */
  canEncode: boolean;
  /** …with a hardware encoder: default speed 4, else 0 (spec: `webcodecsHW ? 4 : 0`). */
  hardware: boolean;
  canToneMap: boolean;
}

export interface ConvertSpec {
  /** null = copy the video track (remux). Only one of width/height is set: the box's short edge. */
  video: {
    width?: number;
    height?: number;
    bitrate: number;
    /** Set only when the source runs faster than the cap. */
    frameRate?: number;
    keyFrameInterval: number;
  } | null;
  /** null = copy the audio track. */
  audio: { passthrough: boolean; bitrate: number; sampleRate: number } | null;
  /** memory = BufferTarget with fastStart 'in-memory'; opfs = StreamTarget to an OPFS file, moov last. */
  target: "memory" | "opfs";
}

export interface ConversionResult {
  blob: Blob;
  /** The encoded output's display size; 0×0 = unknown. */
  width: number;
  height: number;
}

export interface ConversionHandle {
  /** mediabunny `Conversion.isValid` (false also when the video track would be dropped). */
  isValid: boolean;
  /** The source has audio and the conversion would discard it (isValid stays true then). */
  droppedAudio: boolean;
  /** `onProgress` gets 0..1. Rejects after `cancel()`. */
  execute(onProgress: (progress: number) => void): Promise<ConversionResult>;
  cancel(): Promise<void>;
  /** Frees the input and any OPFS file. */
  dispose(): Promise<void>;
}

export interface MediaEngine {
  support(): Promise<EncoderSupport>;
  probe(file: File): Promise<VideoProbe>;
  /** A JPEG frame at `atSec` (clamped into the clip), at most `maxWidth` wide; null = none. */
  poster(file: File, atSec: number, maxWidth: number): Promise<PosterFrame | null>;
  opfsAvailable(): boolean;
  convert(file: File, spec: ConvertSpec): Promise<ConversionHandle>;
}

/** Above this the output streams to OPFS (no faststart) instead of being held in memory. */
export const IN_MEMORY_MAX_BYTES = 300 * 1024 * 1024;
export const POSTER_AT_SEC = 1;
export const POSTER_MAX_WIDTH = 480;

let engineOverride: MediaEngine | null = null;
let enginePromise: Promise<MediaEngine> | null = null;

export function setMediaEngineForTests(engine: MediaEngine | null): void {
  engineOverride = engine;
}

function getEngine(): Promise<MediaEngine> {
  if (engineOverride) return Promise.resolve(engineOverride);
  enginePromise ??= import("./mediabunnyEngine").then((m) => m.mediabunnyEngine);
  return enginePromise;
}

// --- measurements (EWMA) ---------------------------------------------------

const EWMA_ALPHA = 0.3;
let uplinkMbps: number | null = null;
let speedX: number | null = null;

const ewma = (prev: number | null, next: number) => (prev == null ? next : prev + EWMA_ALPHA * (next - prev));

/** Fed by the upload queue from part uploads. */
export function recordUplinkMbps(mbps: number): void {
  if (Number.isFinite(mbps) && mbps > 0) uplinkMbps = ewma(uplinkMbps, mbps);
}

export function recordSpeedX(x: number): void {
  if (Number.isFinite(x) && x > 0) speedX = ewma(speedX, x);
}

export function measurements(): { uplinkMbps: number | null; speedX: number | null } {
  return { uplinkMbps, speedX };
}

export function resetMeasurementsForTests(): void {
  uplinkMbps = null;
  speedX = null;
}

// --- box -------------------------------------------------------------------

/** The 720 box by orientation: portrait caps the width, landscape (and square) the height. */
export function outputBox(displayW: number, displayH: number, shortEdge: number): { width?: number; height?: number } {
  return displayW < displayH ? { width: shortEdge } : { height: shortEdge };
}

const even = (n: number) => Math.max(2, Math.round(n / 2) * 2);

/** The output size for that box (aspect kept, even sides as H.264 wants). */
export function outputSize(displayW: number, displayH: number, shortEdge: number): { width: number; height: number } {
  if (displayW <= 0 || displayH <= 0) return { width: 0, height: 0 };
  if (displayW < displayH) return { width: even(shortEdge), height: even((displayH * shortEdge) / displayW) };
  return { width: even((displayW * shortEdge) / displayH), height: even(shortEdge) };
}

// --- plan ------------------------------------------------------------------

export class VideoTooLargeError extends Error {
  /** BROWSER_LIMIT: over 1 GB and this browser cannot compress it without holding it all in memory. */
  readonly reason: "TOO_LARGE" | "BROWSER_LIMIT";
  constructor(reason: "TOO_LARGE" | "BROWSER_LIMIT" = "TOO_LARGE") {
    super(reason === "BROWSER_LIMIT" ? "Video is too large to send from this browser" : "Video is too large to send");
    this.name = "VideoTooLargeError";
    this.reason = reason;
  }
}

export class NoPosterError extends Error {
  constructor() {
    super("no poster frame");
    this.name = "NoPosterError";
  }
}

export interface PlannedVideo {
  file: File;
  probe: VideoProbe;
  plan: VideoPlan;
  /** Taken from the source right away (shown locally), uploaded after compression. */
  poster: PosterFrame;
}

function unknownProbe(file: File): VideoProbe {
  return {
    source: {
      bytes: file.size,
      durationMs: 0,
      width: 0,
      height: 0,
      rotation: 0,
      videoMime: "",
      audioMime: "",
      audioKbps: null,
      hdr: false,
    },
    displayWidth: 0,
    displayHeight: 0,
    fps: null,
  };
}

/** Probe + poster + decision. Throws NoPosterError when no frame can be drawn (a VIDEO is never sent without one). */
export async function planVideo(file: File): Promise<PlannedVideo> {
  const engine = await getEngine();
  const [probe, support] = await Promise.all([
    engine.probe(file).catch(() => unknownProbe(file)),
    engine.support().catch((): EncoderSupport => ({ canEncode: false, hardware: false, canToneMap: false })),
  ]);
  const m = measurements();
  const plan = decide(probe.source, {
    uplinkMbps: m.uplinkMbps,
    speedX: m.speedX,
    canEncode: support.canEncode,
    canToneMap: support.canToneMap,
    defaultSpeedX: support.hardware ? DEFAULT_SPEED_WEB_HW : 0,
  });
  const poster = await engine.poster(file, POSTER_AT_SEC, POSTER_MAX_WIDTH).catch(() => null);
  if (!poster) throw new NoPosterError();
  return { file, probe, plan, poster };
}

// --- prepare ---------------------------------------------------------------

export interface PreparedVideo {
  blob: Blob;
  filename: string;
  /** Type of the bytes in `blob`. */
  mime: string;
  width: number;
  height: number;
  durationMs: number;
  compressed: boolean;
  cantPlayHint: boolean;
  dispose(): Promise<void>;
}

const EXT_MIME: Record<string, string> = { mp4: "video/mp4", m4v: "video/mp4", mov: "video/quicktime", webm: "video/webm" };

/** The type declared for a video: its own, else (empty or `application/octet-stream`) its extension's. */
export function videoMimeOf(file: File): string {
  const own = (file.type || "").toLowerCase();
  if (own && own !== "application/octet-stream") return own;
  return EXT_MIME[file.name.split(".").pop()?.toLowerCase() ?? ""] ?? "video/mp4";
}

const mp4Name = (name: string) => `${name.replace(/\.[^./]+$/, "") || "video"}.mp4`;

const abortError = () => new DOMException("Aborted", "AbortError");
const noop = async () => {};

function asOriginal(planned: PlannedVideo, cantPlayHint: boolean): PreparedVideo {
  const { file, probe } = planned;
  return {
    blob: file,
    filename: file.name,
    mime: videoMimeOf(file),
    width: probe.displayWidth,
    height: probe.displayHeight,
    durationMs: probe.source.durationMs,
    compressed: false,
    cantPlayHint,
    dispose: noop,
  };
}

function fromFailure(planned: PlannedVideo): PreparedVideo {
  const fallback = afterFailure(planned.probe.source);
  if (fallback.plan !== "ORIGINAL") throw new VideoTooLargeError();
  return asOriginal(planned, fallback.cantPlayHint);
}

/**
 * Opens a conversion that keeps the sound: when the audio would be dropped (no AAC encoder, undecodable
 * audio), an AAC source we meant to re-encode is copied through instead; otherwise null (send the
 * original, never a silent video). Also null when the conversion cannot run (`!isValid`).
 */
async function openConversion(
  engine: MediaEngine,
  file: File,
  src: VideoSource,
  spec: ConvertSpec,
): Promise<ConversionHandle | null> {
  const handle = await engine.convert(file, spec);
  if (handle.isValid && !handle.droppedAudio) return handle;
  await handle.dispose();
  if (!handle.isValid) return null;
  if (spec.audio && !spec.audio.passthrough && src.audioMime === MIME_AAC) {
    const copy = await engine.convert(file, { ...spec, audio: { ...spec.audio, passthrough: true } });
    if (copy.isValid && !copy.droppedAudio) return copy;
    await copy.dispose();
  }
  return null;
}

/** Runs a conversion; resolves null when it was given up (cancelled by the re-estimate). */
async function run(
  handle: ConversionHandle,
  signal: AbortSignal,
  onProgress: (p: number) => void,
  shouldStop: (elapsedMs: number, p: number) => boolean,
): Promise<(ConversionResult & { elapsedMs: number }) | null> {
  const started = Date.now();
  let stopped = false;
  const onAbort = () => void handle.cancel();
  signal.addEventListener("abort", onAbort, { once: true });
  try {
    const result = await handle.execute((p) => {
      onProgress(p);
      if (!stopped && shouldStop(Date.now() - started, p)) {
        stopped = true;
        void handle.cancel();
      }
    });
    return { ...result, elapsedMs: Date.now() - started };
  } catch (err) {
    if (signal.aborted) throw abortError();
    if (stopped) return null;
    throw err;
  } finally {
    signal.removeEventListener("abort", onAbort);
  }
}

/**
 * Makes the bytes to upload for a planned video. Fallbacks never lose the send: `!isValid`, an encode
 * error, a slow encode (re-estimate after 3 s) or an H.264 overshoot (> 1.5× estimate) send the
 * original when it is ≤ 1 GB; a non-H.264 source keeps its H.264 output when it fits (ruling).
 * Rejects with VideoTooLargeError when nothing ≤ 1 GB can be sent, with AbortError on `signal`.
 */
export async function prepareVideo(
  planned: PlannedVideo,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
): Promise<PreparedVideo> {
  if (signal.aborted) throw abortError();
  const { file, probe, plan } = planned;
  const src = probe.source;
  if (plan.plan === "REJECTED") throw new VideoTooLargeError();
  const engine = await getEngine();

  if (plan.plan === "ORIGINAL") {
    // Remux (moov first) only what fits in memory; a remux that cannot run sends the file as it is.
    if (!plan.remux || src.bytes > IN_MEMORY_MAX_BYTES) return asOriginal(planned, plan.cantPlayHint);
    let handle: ConversionHandle | null = null;
    try {
      handle = await openConversion(engine, file, src, { video: null, audio: null, target: "memory" });
      if (!handle) return asOriginal(planned, plan.cantPlayHint);
      const out = await run(handle, signal, onProgress, () => false);
      if (!out) throw new Error("remux stopped");
      const original = asOriginal(planned, plan.cantPlayHint);
      return {
        ...original,
        ...(original.width > 0 && original.height > 0 ? {} : { width: out.width, height: out.height }),
        blob: out.blob,
        filename: mp4Name(file.name),
        mime: "video/mp4",
        dispose: handle.dispose,
      };
    } catch {
      await handle?.dispose();
      if (signal.aborted) throw abortError();
      return asOriginal(planned, plan.cantPlayHint);
    }
  }

  // COMPRESS
  const budget = src.durationMs > 0 ? expectedBytes(src.durationMs) * VideoRule.OVERSHOOT : src.bytes;
  let target: ConvertSpec["target"] = "memory";
  if (budget > IN_MEMORY_MAX_BYTES) {
    // Too big to hold: stream to OPFS (moov last). Without OPFS never encode it in memory: the original
    // goes when it may (<= 1 GB), else this browser cannot send it.
    if (engine.opfsAvailable()) target = "opfs";
    else if (src.bytes <= VideoRule.MAX_BYTES) return fromFailure(planned);
    else throw new VideoTooLargeError("BROWSER_LIMIT");
  }
  const passthrough =
    src.audioMime === "audio/mp4a-latm" && src.audioKbps != null && src.audioKbps <= VideoRule.AUDIO_PASSTHROUGH_MAX_KBPS;
  const spec: ConvertSpec = {
    video: {
      ...outputBox(probe.displayWidth, probe.displayHeight, plan.shortEdge),
      bitrate: plan.videoBps,
      ...(probe.fps != null && probe.fps > plan.fps + 0.5 ? { frameRate: plan.fps } : {}),
      keyFrameInterval: VideoRule.GOP_SEC,
    },
    audio:
      src.audioMime === null
        ? null
        : passthrough
          ? { passthrough: true, bitrate: VideoRule.AUDIO_BPS, sampleRate: VideoRule.AUDIO_SAMPLE_RATE }
          : { passthrough: false, bitrate: VideoRule.AUDIO_BPS, sampleRate: VideoRule.AUDIO_SAMPLE_RATE },
    target,
  };

  let handle: ConversionHandle | null = null;
  try {
    handle = await openConversion(engine, file, src, spec);
    if (signal.aborted) throw abortError();
    if (!handle) return fromFailure(planned);
    const uplink = measurements().uplinkMbps;
    const out = await run(handle, signal, onProgress, (elapsedMs, p) => !keepCompressing(src, elapsedMs, p, uplink));
    if (!out) {
      await handle.dispose();
      return fromFailure(planned);
    }
    if (src.durationMs > 0 && out.elapsedMs > 0) recordSpeedX(src.durationMs / out.elapsedMs);
    const outcome = afterEncode(src, out.blob.size);
    if (outcome === "TOO_LARGE") {
      await handle.dispose();
      throw new VideoTooLargeError();
    }
    if (outcome === "USE_ORIGINAL") {
      await handle.dispose();
      return fromFailure(planned); // an H.264 source <= 1 GB: the original
    }
    // The output's own track gives the size (a failed probe knows none); unknown too → the original.
    const size =
      out.width > 0 && out.height > 0
        ? { width: out.width, height: out.height }
        : outputSize(probe.displayWidth, probe.displayHeight, plan.shortEdge);
    if (size.width <= 0 || size.height <= 0) {
      await handle.dispose();
      return fromFailure(planned);
    }
    return {
      blob: out.blob,
      filename: mp4Name(file.name),
      mime: "video/mp4",
      ...size,
      durationMs: src.durationMs,
      compressed: true,
      cantPlayHint: false,
      dispose: handle.dispose,
    };
  } catch (err) {
    if (err instanceof VideoTooLargeError) throw err;
    await handle?.dispose();
    if (signal.aborted || (err instanceof DOMException && err.name === "AbortError")) throw abortError();
    return fromFailure(planned);
  }
}
